import { type ModalsProps } from "@/features/modals/hooks";
import {
  type Appointment,
  type Customer,
  type Task,
  type hfb,
  isTask,
} from "@repo/api";

import { isAppointment } from "../appointments/utils";
import {
  type CoWorkerCalendarOutlook,
  type CoWorkerScheduleActivity,
  type OutlookEvent,
} from "./types";
import { isOutlookEvent } from "./useUserCalendar";
import { mapToCalendarItem } from "./utils";

export type OpenModalProps =
  | {
      type: "newTask";
      id?: string;
      initialData?: {
        selectedCoworkers?: string;
        selectedTeam?: number;
        startTime?: Date;
        allDay?: boolean;
        cmpCode?: string;
        cmpName?: string;
        hfb?: hfb;
      };
    }
  | {
      type: "task";
      id: string;
    }
  | {
      type: "appointment";
      appointmentId?: number;
      opportunityId?: number;
      dropInId?: number;
      id?: string;
    }
  | {
      type: "outlook";
      item: CoWorkerCalendarOutlook;
    }
  | {
      type: "scheduled_activity";
      item: CoWorkerScheduleActivity;
    };

export const openCalendarModalCreator = (
  items: (Appointment | Task)[],
  pushModal: ModalsProps["pushModal"],
  emailId?: string[],
  mutateSearchAppointments?: () => void,
  onBookFollowUp?: (appointment: Appointment, customer?: Customer) => void
) => {
  return (props: OpenModalProps) => {
    if (props.type === "task") {
      const task = items.find((item) => item.id.toString() === props.id);
      if (isTask(task)) {
        pushModal({
          type: "TASK",
          task,
          mutateSearchAppointments,
          opportunityId: task.opportunityId,
          context: task.opportunityId ? "PROJECT" : "CALENDAR",
        });
      }
    } else if (props.type === "appointment") {
      const item = props.appointmentId
        ? items.find(
            (i) =>
              (isTask(i) ? i.appointmentNumber : i.appointmentId) ===
              props.appointmentId
          )
        : items.find((i) => i.id === props.id);

      pushModal({
        type: "CALENDAR_APPOINTMENT",
        appointment: item as Appointment,
        dropInId: props.dropInId,
        mutateSearchAppointments,
        onBookFollowUp,
      });
    } else if (props.type === "newTask") {
      pushModal({
        type: "TASK",
        initialData: {
          startTime: props.initialData?.startTime,
          selectedCoworkers: props.initialData?.selectedCoworkers
            ? [props.initialData?.selectedCoworkers]
            : emailId,
          selectedTeam: props.initialData?.selectedTeam,
          allDay: props.initialData?.allDay,
          cmpCode: props.initialData?.cmpCode,
          cmpName: props.initialData?.cmpName,
          hfb: props.initialData?.hfb,
        },
        mutateSearchAppointments,
        context: "CALENDAR",
      });
    } else if (props.type === "outlook") {
      pushModal({ type: "OUTLOOK_EVENT", item: props.item });
    }
  };
};

export const openCalendarModal = (
  item: Task | Appointment | OutlookEvent,
  openModal: (props: OpenModalProps) => void
) => {
  if (isTask(item)) {
    openModal?.({ type: "task", id: String(item.id) });
  } else if (isAppointment(item)) {
    openModal?.({
      type: "appointment",
      opportunityId: item.opportunityId,
      appointmentId: item.appointmentId,
      dropInId: item.appointmentId,
      id: item.id,
    });
  } else if (isOutlookEvent(item)) {
    const mappedItem = mapToCalendarItem([item])?.[0];
    openModal?.({
      type: "outlook",
      item: mappedItem as CoWorkerCalendarOutlook,
    });
  }
};
